import { useMemo, useState } from 'react'
import AutoAwesomeMosaicRoundedIcon from '@mui/icons-material/AutoAwesomeMosaicRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import { TextInput } from '../../Common/TextInput'
import { useDesignAssets } from '../../../hooks/useDesignAssets'
import { designAssetToDataContext } from '../workspaceAssetContext'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'

export interface DesignsPaneProps {
  workspaceId: string
  basket: DataContext[]
  onToggle: (context: DataContext) => void
  mode?: 'aiContext' | 'itemAttachment'
  onAttachDesign?: (designId: string) => Promise<void>
  onDetachDesign?: (designId: string) => Promise<void>
  attachedDesignIds?: Set<string>
}

export const DesignsPane = ({
  workspaceId,
  basket,
  onToggle,
  mode = 'aiContext',
  onAttachDesign,
  onDetachDesign,
  attachedDesignIds,
}: DesignsPaneProps) => {
  const { t } = useNls()
  const [search, setSearch] = useState('')
  const basketIdSet = useMemo(() => new Set(basket.map((item) => item.id)), [basket])
  const designsState = useDesignAssets(workspaceId)

  const filteredDesigns = useMemo(() => {
    if (!search.trim()) return designsState.items
    const q = search.toLowerCase().trim()
    return designsState.items.filter((asset) =>
      (asset.title || '').toLowerCase().includes(q) ||
      (asset.mode || '').toLowerCase().includes(q) ||
      (asset.boundIntent || '').toLowerCase().includes(q),
    )
  }, [designsState.items, search])

  return (
    <div className="ctxPane ctxPane--designs">
      <div className="ctxPane__toolbar">
        <TextInput
          type="search"
          leadingIcon={<SearchRoundedIcon fontSize="small" />}
          placeholder={t('NLSAiContextPickerSearchDesigns')}
          value={search}
          onChange={setSearch}
          aria-label={t('NLSAiContextPickerSearchDesigns')}
        />
      </div>

      {designsState.loading ? (
        <div className="ctxPane__loader">{t('NLSAiContextPickerLoadingDesigns')}</div>
      ) : filteredDesigns.length === 0 ? (
        <div className="ctxPane__empty">
          <AutoAwesomeMosaicRoundedIcon style={{ fontSize: 36, opacity: 0.3 }} />
          <p>
            {designsState.items.length === 0
              ? t('NLSAiContextPickerNoDesigns')
              : t('NLSAiContextPickerNoDesignsMatch')}
          </p>
        </div>
      ) : (
        <div className="ctxPane__designGrid">
          {filteredDesigns.map((asset) => {
            const contextId = `workspace-design:${asset.id}`
            const isItemAttachmentMode = mode === 'itemAttachment'
            const isSelected = isItemAttachmentMode
              ? Boolean(attachedDesignIds?.has(asset.id))
              : basketIdSet.has(contextId)

            const handleCardClick = async () => {
              if (isItemAttachmentMode) {
                if (isSelected) {
                  await onDetachDesign?.(asset.id)
                } else {
                  await onAttachDesign?.(asset.id)
                }
              } else {
                onToggle(designAssetToDataContext(asset))
              }
            }

            return (
              <div
                key={asset.id}
                className={`ctxPane__designCard${isSelected ? ' is-selected' : ''}`}
                onClick={() => void handleCardClick()}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault()
                    void handleCardClick()
                  }
                }}
              >
                <div className="ctxPane__designCardHeader">
                  <AccountTreeRoundedIcon fontSize="small" />
                  <div className="ctxPane__designCheckbox">
                    {isSelected && <CheckRoundedIcon style={{ fontSize: 12 }} />}
                  </div>
                </div>
                <span className="ctxPane__designTitle" title={asset.title || t('NLSAiContextPickerUntitledDesign')}>
                  {asset.title || t('NLSAiContextPickerUntitledDesign')}
                </span>
                <span className="ctxPane__designMeta">
                  {asset.mode} · {asset.shapes.length} shape{asset.shapes.length === 1 ? '' : 's'}
                  {asset.boundIntent ? ` · ${asset.boundIntent}` : ''}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
